import type { StoredSession } from '~/gql/sessions/sessions'
import { useMutation } from '@apollo/client/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '~/components/ui/dialog'
import { Input } from '~/components/ui/input'
import { Label } from '~/components/ui/label'
import { GQL_DUPLICATE_SESSION } from '~/gql/sessions/sessions'

type IProps = {
  session: Pick<StoredSession, 'id' | 'name'>
  onCancel: () => void
  onDuplicated: (copy: StoredSession) => void
}

/**
 * Dupliquer une session pour un nouveau groupe (plan 062) : on nomme la copie,
 * l'API reprend sa configuration, sans dates ni apprenants.
 */
function DuplicateSessionDialog({ session, onCancel, onDuplicated }: IProps) {
  const { t } = useTranslation()
  const [name, setName] = useState(() => t('sessions.duplicateDefaultName', { name: session.name }))
  const [duplicateSession, { loading }] = useMutation(GQL_DUPLICATE_SESSION)

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    try {
      const { data } = await duplicateSession({ variables: { id: session.id, name: name.trim() } })
      if (!data) return
      toast.success(t('sessions.duplicated'))
      onDuplicated(data.duplicateSession)
    } catch {
      toast.error(t('sessions.duplicateFailed'))
    }
  }

  return (
    <Dialog open onOpenChange={open => !open && onCancel()}>
      <DialogContent>
        <form onSubmit={submit} className="flex flex-col gap-4">
          <DialogHeader>
            <DialogTitle>{t('sessions.duplicateTitle', { name: session.name })}</DialogTitle>
            <DialogDescription>{t('sessions.duplicateBody')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="duplicateSessionName" required>{t('sessions.duplicateName')}</Label>
            <Input id="duplicateSessionName" required value={name} onChange={e => setName(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>{t('common.cancel')}</Button>
            <Button type="submit" disabled={!name.trim() || loading}>{t('sessions.duplicate')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default DuplicateSessionDialog
